package org.example.abigail

import androidx.compose.foundation.layout.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.window.Dialog

import features.auth.AuthViewModel
import features.auth.LoginScreen
import features.booking.AdminViewModel
import features.booking.AdminScreen
import features.booking.BookingViewModel
import features.booking.BookingScreen
import features.booking.BookingRepositoryImpl
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.auth
import features.booking.CatalogRepositoryImpl
import features.booking.ChoferScreen
import features.booking.ChoferViewModel
import features.booking.NotificacionViewModel
import features.booking.TrackingScreen
import features.booking.TrackingViewModel
import features.booking.UserRepositoryImpl
import features.payment.PaymentRepositoryImpl
import features.payment.PaymentScreen
import features.payment.PaymentViewModel
import features.payment.SpeiConfirmationScreen
import features.payment.OxxoConfirmationScreen
import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.expandVertically
import androidx.compose.animation.shrinkVertically
import androidx.compose.foundation.background
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.CheckCircle
import androidx.compose.material.icons.filled.Cancel
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.launch
import kotlinx.coroutines.flow.first
import navigation.MainViewModel
import navigation.Screen
import network.ConexionViewModel
import network.VersionViewModel
import network.AppConfig

@Composable
fun App() {
    var isFirebaseReady by remember { mutableStateOf(false) }
    var isCheckingSession by remember { mutableStateOf(true) }
    var isAdminLoggedIn by remember { mutableStateOf(false) }
    var rolUsuario by remember { mutableStateOf("") } // rol del usuario logueado (para el botón de panel)
    val scope = rememberCoroutineScope()

    val userRepository = remember { UserRepositoryImpl() }
    val catalogRepository = remember { CatalogRepositoryImpl() }
    val bookingRepository = remember { BookingRepositoryImpl() }

    // ViewModels que siempre se necesitan
    val authViewModel = remember { AuthViewModel(userRepository) }
    val bookingViewModel = remember { BookingViewModel(catalogRepository, bookingRepository) }
    val notificacionViewModel = remember { NotificacionViewModel() }
    val trackingViewModel = remember { TrackingViewModel() }
    val conexionViewModel = remember { ConexionViewModel() }
    val versionViewModel = remember { VersionViewModel() }

    // FIX #4: ViewModels lazy con referencia al Lazy para poder verificar isInitialized()
    val adminViewModelLazy = remember { lazy { AdminViewModel(catalogRepository, userRepository, bookingRepository) } }
    val adminViewModel by adminViewModelLazy
    val paymentViewModelLazy = remember { lazy { PaymentViewModel(PaymentRepositoryImpl()) } }
    val paymentViewModel by paymentViewModelLazy
    val choferViewModelLazy = remember { lazy { ChoferViewModel(bookingRepository) } }
    val choferViewModel by choferViewModelLazy

    // FIX #4: Limpiar TODOS los coroutine scopes cuando App sale de composición
    DisposableEffect(Unit) {
        onDispose {
            authViewModel.onCleared()
            bookingViewModel.cleanup() // FIX #5: limpiar singleton antes de cancel scope
            bookingViewModel.onCleared()
            notificacionViewModel.onCleared()
            trackingViewModel.onCleared()
            conexionViewModel.detener()
            conexionViewModel.onCleared()
            versionViewModel.onCleared()
            // Lazy ViewModels: solo limpiar si fueron inicializados
            if (adminViewModelLazy.isInitialized()) adminViewModelLazy.value.onCleared()
            if (paymentViewModelLazy.isInitialized()) paymentViewModelLazy.value.onCleared()
            if (choferViewModelLazy.isInitialized()) choferViewModelLazy.value.onCleared()
        }
    }


    var showLoginPopup by remember { mutableStateOf(false) }

    val mainViewModel = remember { MainViewModel() }
    val currentScreen by mainViewModel.currentScreen.collectAsState()

    // FIX #20: Registrar listener para botón back del navegador
    LaunchedEffect(Unit) {
        registrarOnBackNavigation {
            mainViewModel.navigateBack()
        }
    }

    // Return del Checkout de Stripe: mostrar aviso al usuario según el query ?pago=...
    var mostrarAvisoPago by remember { mutableStateOf<String?>(null) }
    var sessionIdPago by remember { mutableStateOf("") }
    var descargandoBoletos by remember { mutableStateOf(false) }
    var msgDescarga by remember { mutableStateOf("") }
    // Pantalla intermedia de compra exitosa (tarjeta): avisa y lleva a los boletos.
    var mostrandoExitoTarjeta by remember { mutableStateOf(false) }
    var errorBoletos by remember { mutableStateOf("") }

    LaunchedEffect(Unit) {
        // Si ESTA es la pestaña que abrimos para pagar, no duplicamos el aviso: la pestaña
        // original ya detecta la confirmación por el listener y ahí está la descarga.
        // Se cierra sola para no dejar al usuario con dos pestañas.
        if (esPestanaDePago()) {
            cerrarPestanaActual()
            return@LaunchedEffect
        }
        // Volver a entrar con ?boletos=... reabre los boletos guardados (link compartible).
        val sesionBoletos = leerSessionIdBoletos()
        if (sesionBoletos.isNotBlank()) {
            sessionIdPago = sesionBoletos
            if (!abrirVistaBoletosDeSesion(sesionBoletos)) {
                errorBoletos = "No encontramos boletos para este enlace."
            }
            return@LaunchedEffect
        }
        val estado = leerYLimpiarQueryPago()
        if (estado.isNotBlank()) {
            sessionIdPago = leerSessionIdCheckout()   // leído antes de limpiar
            // Tarjeta exitosa: pantalla de éxito + auto-redirección a los boletos.
            if (estado == "exitoso" && sessionIdPago.isNotBlank()) {
                mostrandoExitoTarjeta = true
            } else {
                mostrarAvisoPago = estado
            }
        }
    }

    if (mostrandoExitoTarjeta) {
        PantallaCompraExitosa(
            error = errorBoletos,
            onListo = {
                scope.launch {
                    if (abrirVistaBoletosDeSesion(sessionIdPago)) {
                        mostrandoExitoTarjeta = false
                    } else {
                        errorBoletos = "Tu pago se confirmó, pero los boletos aún se están generando. " +
                            "Revisa tu correo o intenta de nuevo en un momento."
                    }
                }
            }
        )
    }
    if (mostrarAvisoPago != null) {
        val esExitoso = mostrarAvisoPago == "exitoso"
        androidx.compose.material3.AlertDialog(
            onDismissRequest = { mostrarAvisoPago = null },
            icon = {
                androidx.compose.material3.Icon(
                    if (esExitoso) Icons.Filled.CheckCircle else Icons.Filled.Cancel,
                    contentDescription = null,
                    tint = if (esExitoso) androidx.compose.ui.graphics.Color(0xFF2E7D32) else androidx.compose.ui.graphics.Color(0xFFC62828)
                )
            },
            title = { androidx.compose.material3.Text(if (esExitoso) "¡Pago procesado!" else "Pago cancelado") },
            text = {
                androidx.compose.foundation.layout.Column {
                    androidx.compose.material3.Text(
                        if (esExitoso) "Tu pago con tarjeta se procesó correctamente. Tus boletos con código QR se enviaron a tu correo electrónico."
                        else "El pago fue cancelado. Los asientos siguen disponibles si quieres intentar de nuevo."
                    )
                    if (esExitoso) {
                        androidx.compose.foundation.layout.Spacer(Modifier.height(10.dp))
                        androidx.compose.material3.Text(
                            "¿No te llegó el correo (correo mal escrito, spam)? Descárgalos aquí:",
                            style = androidx.compose.material3.MaterialTheme.typography.bodySmall,
                            color = androidx.compose.ui.graphics.Color.Gray
                        )
                        if (msgDescarga.isNotBlank()) {
                            androidx.compose.foundation.layout.Spacer(Modifier.height(6.dp))
                            androidx.compose.material3.Text(msgDescarga, style = androidx.compose.material3.MaterialTheme.typography.labelSmall, color = androidx.compose.ui.graphics.Color(0xFFC62828))
                        }
                    }
                }
            },
            confirmButton = {
                if (esExitoso && sessionIdPago.isNotBlank()) {
                    androidx.compose.material3.Button(
                        onClick = {
                            scope.launch {
                                descargandoBoletos = true; msgDescarga = ""
                                val ok = descargarBoletosDeSesion(sessionIdPago)
                                if (!ok) msgDescarga = "Aún no están listos tus boletos. Espera unos segundos y reintenta."
                                descargandoBoletos = false
                            }
                        },
                        enabled = !descargandoBoletos
                    ) {
                        androidx.compose.material3.Text(if (descargandoBoletos) "Preparando…" else "Descargar boletos")
                    }
                } else {
                    androidx.compose.material3.TextButton(onClick = { mostrarAvisoPago = null }) {
                        androidx.compose.material3.Text("Entendido")
                    }
                }
            },
            dismissButton = {
                if (esExitoso && sessionIdPago.isNotBlank()) {
                    androidx.compose.material3.TextButton(onClick = { mostrarAvisoPago = null }) {
                        androidx.compose.material3.Text("Cerrar")
                    }
                }
            }
        )
    }

    // Si la sesión de selección expira (asientos liberados) mientras el usuario está en el
    // flujo de pago (tarjeta/SPEI/OXXO), regresarlo a la pantalla de inicio en vez de dejarlo
    // atrapado en la selección de método. El aviso "Tu selección expiró" se muestra en inicio.
    val sesionExpirada by bookingViewModel.sesionExpiradaPorAbandono.collectAsState()
    LaunchedEffect(sesionExpirada) {
        if (sesionExpirada && (currentScreen is Screen.Payment ||
                currentScreen is Screen.SpeiConfirmation || currentScreen is Screen.OxxoConfirmation)) {
            if (paymentViewModelLazy.isInitialized()) paymentViewModel.resetearEstado()
            mainViewModel.navigateTo(Screen.Cliente)
        }
    }

    // Sincronización en vivo de la config de pagos (apagar Stripe, CLABE, etc.) en TODAS las plataformas
    LaunchedEffect(Unit) {
        network.ConfigPagos.escuchar()
    }

    LaunchedEffect(Unit) {
        org.example.abigail.inicializarFirebase()
        conexionViewModel.iniciarMonitoreo()
        versionViewModel.verificarVersion()
        network.ConfigPagos.cargar()
        isFirebaseReady = true

        try {
            // Esperar a que Firebase RESTAURE la sesión persistida.
            val usuarioActivo = Firebase.auth.authStateChanged.first()
            if (usuarioActivo != null) {
                // Sesión persistente. Si es anonymous, no hay rol → deja rolUsuario vacío
                // (el UI trata al usuario como comprador). Si es admin/chofer, carga rol.
                if (usuarioActivo.isAnonymous) {
                    rolUsuario = ""
                } else {
                    val rol = userRepository.getUserRole(usuarioActivo.uid)
                    val nombre = userRepository.getUserName(usuarioActivo.uid)
                    authViewModel.nombreUsuario.value = nombre ?: usuarioActivo.email ?: ""
                    rolUsuario = rol ?: ""
                    isAdminLoggedIn = rol == "desarrollador" || rol == "master" || rol == "admin_general" || rol == "admin"
                }
            } else {
                // Sin sesión: firmar anónimo para que el comprador pueda usar CFs (SPEI/OXXO/tarjeta)
                // sin registrarse ni loguearse. El rol "cliente" fue eliminado del sistema.
                try {
                    Firebase.auth.signInAnonymously()
                } catch (_: Exception) { /* si falla, la UI seguirá funcionando parcialmente */ }
            }
        } catch (e: Exception) {
        } finally {
            isCheckingSession = false
        }
    }

    if (!isFirebaseReady || isCheckingSession) {
        Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
            CircularProgressIndicator(color = Color(0xFF00ACC1))
        }
    } else {
        MaterialTheme {
            Surface(modifier = Modifier.fillMaxSize()) {
                Box(modifier = Modifier.fillMaxSize()) {
                    val estaConectado by conexionViewModel.estaConectado.collectAsState()
                    val requiereActualizacion by versionViewModel.requiereActualizacion.collectAsState()
                    val mensajeActualizacion by versionViewModel.mensajeActualizacion.collectAsState()

                    LaunchedEffect(currentScreen) {
                        val usuario = Firebase.auth.currentUser
                        if (usuario == null && (currentScreen is Screen.Admin || currentScreen is Screen.Chofer)) {
                            isAdminLoggedIn = false
                            mainViewModel.navigateTo(Screen.Cliente)
                        }
                    }
                    when (currentScreen) {
                        is Screen.Cliente -> {
                            BookingScreen(
                                viewModel = bookingViewModel,
                                isAdminLoggedIn = isAdminLoggedIn,
                                onBackClick = { },
                                onBoletoComprado = { mainViewModel.navigateTo(Screen.Payment) },
                                onOpenLoginClick = {
                                    when {
                                        rolUsuario == "desarrollador" || rolUsuario == "master" || rolUsuario == "admin_general" || rolUsuario == "admin" ->
                                            mainViewModel.navigateTo(Screen.Admin)
                                        rolUsuario == "chofer" || rolUsuario == "chofer_admin" ->
                                            mainViewModel.navigateTo(Screen.Chofer)
                                        else -> showLoginPopup = true
                                    }
                                },
                                onOpenTracking = { mainViewModel.navigateTo(Screen.Tracking) }
                            )
                        }

                        is Screen.Admin -> {
                            AdminScreen(
                                viewModel = adminViewModel,
                                notificacionViewModel = notificacionViewModel,
                                onNavigateToClient = { mainViewModel.navigateTo(Screen.Cliente) },
                                onLogout = {
                                    scope.launch {
                                        bookingViewModel.cancelarReservaActual()
                                        authViewModel.limpiarSesion()
                                        Firebase.auth.signOut()
                                        // Firmar anónimo inmediatamente: el usuario sigue navegando
                                        // como comprador sin cuenta (Anonymous Auth).
                                        try { Firebase.auth.signInAnonymously() } catch (_: Exception) {}
                                        isAdminLoggedIn = false
                                        rolUsuario = ""
                                        mainViewModel.navigateTo(Screen.Cliente)
                                    }
                                },
                                onVerPanelChofer = { uid, nombre ->
                                    mainViewModel.verPanelComoChofer(uid, nombre)
                                }
                            )
                        }

                        is Screen.Payment -> {
                            PaymentScreen(
                                bookingViewModel = bookingViewModel,
                                paymentViewModel = paymentViewModel,
                                onBackClick = { mainViewModel.navigateTo(Screen.Cliente) },
                                onSpeiGenerated = { mainViewModel.navigateTo(Screen.SpeiConfirmation) },
                                onOxxoGenerated = { mainViewModel.navigateTo(Screen.OxxoConfirmation) }
                            )
                        }

                        is Screen.SpeiConfirmation -> {
                            SpeiConfirmationScreen(
                                bookingViewModel = bookingViewModel,
                                paymentViewModel = paymentViewModel,
                                onFinishClick = {
                                    bookingViewModel.finalizarCompraYSalir()
                                    paymentViewModel.resetearEstado()
                                    mainViewModel.navigateTo(Screen.Cliente)
                                }
                            )
                        }

                        is Screen.OxxoConfirmation -> {
                            OxxoConfirmationScreen(
                                bookingViewModel = bookingViewModel,
                                paymentViewModel = paymentViewModel,
                                onFinishClick = {
                                    bookingViewModel.finalizarCompraYSalir()
                                    paymentViewModel.resetearEstado()
                                    mainViewModel.navigateTo(Screen.Cliente)
                                }
                            )
                        }
                        is Screen.Tracking -> {
                            DisposableEffect(Unit) {
                                onDispose { trackingViewModel.limpiar() }
                            }
                            TrackingScreen(
                                viewModel = trackingViewModel,
                                onVolver = { mainViewModel.navigateTo(Screen.Cliente) }
                            )
                        }

                        is Screen.Chofer -> {
                            val impUid by mainViewModel.impersonarChoferUid.collectAsState()
                            val impNombre by mainViewModel.impersonarChoferNombre.collectAsState()
                            ChoferScreen(
                                viewModel = choferViewModel,
                                nombreChofer = authViewModel.nombreUsuario.value,
                                onLogout = {
                                    scope.launch {
                                        authViewModel.limpiarSesion()
                                        Firebase.auth.signOut()
                                        try { Firebase.auth.signInAnonymously() } catch (_: Exception) {}
                                        rolUsuario = ""
                                        mainViewModel.navigateTo(Screen.Cliente)
                                    }
                                },
                                impersonarUid = impUid,
                                impersonarNombre = impNombre,
                                onSalirEspectador = {
                                    // Admin regresa a su panel; limpiamos los params de impersonación.
                                    mainViewModel.limpiarImpersonarChofer()
                                    mainViewModel.navigateTo(Screen.Admin)
                                }
                            )
                        }
                    }

                    // Banner offline — visible encima de cualquier pantalla
                    AnimatedVisibility(
                        visible = !estaConectado,
                        enter = expandVertically(),
                        exit = shrinkVertically(),
                        modifier = Modifier.align(Alignment.TopCenter)
                    ) {
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .background(Color(0xFFFF5252))
                                .padding(vertical = 8.dp, horizontal = 16.dp),
                            contentAlignment = Alignment.Center
                        ) {
                            Text(
                                "Sin conexión a internet. Verificando...",
                                color = Color.White,
                                style = MaterialTheme.typography.bodySmall,
                                textAlign = TextAlign.Center
                            )
                        }
                    }

                    // Banner de versión obsoleta — modo lectura
                    AnimatedVisibility(
                        visible = requiereActualizacion,
                        enter = expandVertically(),
                        exit = shrinkVertically(),
                        modifier = Modifier.align(Alignment.TopCenter).padding(top = if (!estaConectado) 36.dp else 0.dp)
                    ) {
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .background(Color(0xFFFF8F00))
                                .padding(vertical = 10.dp, horizontal = 16.dp),
                            contentAlignment = Alignment.Center
                        ) {
                            Column(horizontalAlignment = Alignment.CenterHorizontally) {
                                Text(
                                    "Versión desactualizada — Modo lectura",
                                    color = Color.White,
                                    style = MaterialTheme.typography.bodySmall,
                                    textAlign = TextAlign.Center,
                                    fontWeight = androidx.compose.ui.text.font.FontWeight.Bold
                                )
                                Text(
                                    mensajeActualizacion.ifBlank { "Actualiza la app para poder realizar cambios. (v${AppConfig.VERSION_NAME})" },
                                    color = Color.White.copy(alpha = 0.9f),
                                    style = MaterialTheme.typography.labelSmall,
                                    textAlign = TextAlign.Center
                                )
                            }
                        }
                    }

                    if (showLoginPopup) {
                        Dialog(onDismissRequest = { showLoginPopup = false }) {
                            LoginScreen(
                                viewModel = authViewModel,
                                onLoginSuccess = { rol ->
                                    showLoginPopup = false
                                    rolUsuario = rol
                                    if (rol == "desarrollador" || rol == "master" || rol == "admin_general" || rol == "admin") {
                                        isAdminLoggedIn = true
                                        mainViewModel.navigateTo(Screen.Admin)
                                    } else if (rol == "chofer" || rol == "chofer_admin") {
                                        mainViewModel.navigateTo(Screen.Chofer)
                                    }
                                }
                            )
                        }
                    }
                }
            }
        }
    }
}

/**
 * Descarga/imprime los boletos de una compra con tarjeta, leyendo el espejo
 * pagos_confirmados/{sessionId} y los códigos QR reales de verificaciones.
 * Sirve como respaldo si el correo no llegó (correo mal escrito, spam).
 * @return true si generó los boletos; false si aún no están listos.
 */
/**
 * Abre la vista de boletos en pantalla para un pago confirmado.
 *
 * Reintenta: el webhook tarda 1-3 s en confirmar y generar el QR, así que justo después de
 * pagar el boleto puede no existir todavía. Devuelve false si tras ~15 s sigue sin aparecer.
 */
suspend fun abrirVistaBoletosDeSesion(sessionId: String): Boolean {
    if (sessionId.isBlank()) return false
    // Hasta ~60 s: más vale esperar que mandar al usuario al inicio sin sus boletos. Los
    // primeros intentos son rápidos porque lo normal es que ya estén en 1-3 s.
    repeat(24) { intento ->
        val html = construirBoletosHtmlDeSesion(sessionId)
        if (html != null) {
            mostrarBoletosEnPagina(features.booking.envolverBoletosParaPantalla(html), sessionId)
            return true
        }
        kotlinx.coroutines.delay(if (intento < 5) 1000L else 3000L)
    }
    return false
}

/**
 * Pantalla intermedia tras pagar con tarjeta: confirma la compra y lleva a los boletos.
 *
 * Existe porque el webhook tarda 1-3 s en confirmar y generar el QR. Antes ese hueco hacía
 * fallar la descarga con "aún no están listos"; aquí la espera es parte del flujo.
 * Redirige sola, con botón por si la redirección no ocurre.
 */
@Composable
private fun PantallaCompraExitosa(error: String, onListo: () -> Unit) {
    // Arranca la búsqueda de inmediato: la propia búsqueda ya reintenta mientras el webhook
    // confirma, así que no hace falta una cuenta regresiva antes de empezar.
    LaunchedEffect(Unit) { onListo() }
    Dialog(onDismissRequest = {}) {
        Surface(shape = androidx.compose.foundation.shape.RoundedCornerShape(20.dp), color = Color.White) {
            Column(
                modifier = Modifier.padding(28.dp).fillMaxWidth(),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Icon(
                    Icons.Filled.CheckCircle, contentDescription = null,
                    tint = Color(0xFF2E7D32), modifier = Modifier.size(56.dp)
                )
                Spacer(Modifier.height(12.dp))
                Text("¡Compra exitosa!", style = MaterialTheme.typography.headlineSmall, color = Color(0xFF00205B))
                Spacer(Modifier.height(8.dp))
                Text(
                    if (error.isBlank()) "Te llevamos a tus boletos…" else error,
                    style = MaterialTheme.typography.bodyMedium,
                    color = if (error.isBlank()) Color.Gray else Color(0xFFC62828),
                    textAlign = TextAlign.Center
                )
                Spacer(Modifier.height(6.dp))
                Text(
                    "También los enviamos a tu correo.",
                    style = MaterialTheme.typography.bodySmall, color = Color.Gray, textAlign = TextAlign.Center
                )
                Spacer(Modifier.height(18.dp))
                if (error.isBlank()) {
                    CircularProgressIndicator(modifier = Modifier.size(26.dp), strokeWidth = 3.dp, color = Color(0xFF00205B))
                    Spacer(Modifier.height(14.dp))
                }
                Button(
                    onClick = onListo,
                    colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF00205B)),
                    modifier = Modifier.fillMaxWidth()
                ) { Text(if (error.isBlank()) "Ver mis boletos" else "Reintentar") }
            }
        }
    }
}

/**
 * Construye el HTML de los boletos de un pago confirmado, o null si aún no está listo.
 *
 * Fuente única para las dos salidas: la pantalla de boletos en pantalla (screenshot) y la
 * descarga/impresión. Usa el mismo generador que el correo, así los tres coinciden.
 */
suspend fun construirBoletosHtmlDeSesion(sessionId: String): String? {
    return try {
        val db = network.FirebaseClient.database
        val confSnap = db.reference("pagos_confirmados/$sessionId").valueEvents.first()
        if (!confSnap.exists) return null
        val ruta = try { confSnap.child("ruta").value<String>() } catch (_: Exception) { "" }
        val fecha = try { confSnap.child("fecha").value<String>() } catch (_: Exception) { "" }
        val horario = try { confSnap.child("horario").value<String>() } catch (_: Exception) { "" }
        val reservaId = try { confSnap.child("reservaId").value<String>() } catch (_: Exception) { "" }
        if (ruta.isBlank() || fecha.isBlank() || horario.isBlank()) return null

        // Pasajeros del pago.
        data class Pax(
            val nombre: String, val asiento: String, val parada: String, val telefono: String,
            val maletasExtras: Int, val llevaMascota: Boolean
        )
        val pax = mutableListOf<Pax>()
        if (confSnap.child("pasajeros").exists) {
            confSnap.child("pasajeros").children.forEach { p ->
                val nom = try { p.child("nombre").value<String>() } catch (_: Exception) { "" }
                val asi = try { p.child("asiento").value<Int>().toString() } catch (_: Exception) {
                    try { p.child("asiento").value<String>() } catch (_: Exception) { "" }
                }
                val par = try { p.child("parada").value<String>() } catch (_: Exception) { "" }
                val tel = try { p.child("telefono").value<String>() } catch (_: Exception) { "" }
                val mal = try { p.child("maletasExtras").value<Int>() } catch (_: Exception) { 0 }
                val mas = try { p.child("llevaMascota").value<Boolean>() } catch (_: Exception) { false }
                if (nom.isNotBlank()) pax.add(Pax(nom, asi, par, tel, mal, mas))
            }
        }
        if (pax.isEmpty()) return null

        // Punto de subida que eligió el cliente + su hora/coords (catalogos es de lectura pública).
        val puntoSubida = try { confSnap.child("puntoSubida").value<String>() } catch (_: Exception) { "" }
        var horaSubida = ""
        var mapsUrl = ""
        if (puntoSubida.isNotBlank()) {
            try {
                val infoSnap = db.reference("catalogos/puntosSubidaInfo/$puntoSubida").valueEvents.first()
                if (infoSnap.exists) {
                    horaSubida = try { infoSnap.child("hora").value<String>() } catch (_: Exception) { "" }
                    val lat = try { infoSnap.child("lat").value<Double>() } catch (_: Exception) { 0.0 }
                    val lng = try { infoSnap.child("lng").value<Double>() } catch (_: Exception) { 0.0 }
                    if (lat != 0.0 && lng != 0.0) mapsUrl = "https://maps.google.com/?q=$lat,$lng"
                }
            } catch (_: Exception) {}
        }

        // Links de Maps de las bajadas (cada pasajero puede bajar en otro lado). Un solo read.
        val mapsBajada = mutableMapOf<String, String>()
        try {
            val pInfo = db.reference("catalogos/paradasInfo").valueEvents.first()
            if (pInfo.exists) {
                pInfo.children.forEach { c ->
                    val nom = c.key ?: return@forEach
                    val lat = try { c.child("lat").value<Double>() } catch (_: Exception) { 0.0 }
                    val lng = try { c.child("lng").value<Double>() } catch (_: Exception) { 0.0 }
                    if (lat != 0.0 && lng != 0.0) mapsBajada[nom] = "https://maps.google.com/?q=$lat,$lng"
                }
            }
        } catch (_: Exception) {}

        // Pago de sandbox: el boleto se marca para que nadie lo confunda con uno real. Ocupa
        // asiento de verdad y el QR es válido, así que sin marca es indistinguible.
        val esPrueba = try { confSnap.child("esPrueba").value<Boolean>() } catch (_: Exception) { false }

        // Total por pasajero: el correo pinta el precio individual, no el total del pago.
        val montoTotal = try { confSnap.child("monto").value<Double>().toInt() } catch (_: Exception) {
            try { confSnap.child("monto").value<Int>() } catch (_: Exception) { 0 }
        }
        val precioPorPax = if (pax.isNotEmpty()) montoTotal / pax.size else montoTotal

        // QR reales por asiento (los que escanea el chofer).
        val qrPorAsiento = mutableMapOf<String, String>()
        try {
            val verifSnap = db.reference("viajes/$ruta/$fecha/$horario/verificaciones").valueEvents.first()
            if (verifSnap.exists) {
                verifSnap.children.forEach { v ->
                    val rid = try { v.child("reservaId").value<String>() } catch (_: Exception) { "" }
                    if (reservaId.isBlank() || rid == reservaId) {
                        val asi = try { v.child("asiento").value<Int>().toString() } catch (_: Exception) {
                            try { v.child("asiento").value<String>() } catch (_: Exception) { "" }
                        }
                        if (asi.isNotBlank()) qrPorAsiento[asi] = v.key ?: ""
                    }
                }
            }
        } catch (_: Exception) {}

        // Mismo boleto que llega por correo (ver BoletoHtml.kt). Si no hay QR real todavía,
        // se reconstruye desde el timestamp del pago para no descargar un boleto sin código.
        val timestampPago = try { confSnap.child("timestamp").value<String>() } catch (_: Exception) { "0" }
        val boletos = pax.joinToString("") { p ->
            features.booking.generarHtmlBoletoSmartVan(
                nombre = p.nombre,
                asiento = p.asiento,
                parada = p.parada,
                telefono = p.telefono,
                maletasExtras = p.maletasExtras,
                llevaMascota = p.llevaMascota,
                ruta = ruta,
                fecha = fecha,
                horario = horario,
                totalPagado = precioPorPax,
                codigoQR = qrPorAsiento[p.asiento].orEmpty().ifBlank { "SMARTVAN-$timestampPago-${p.asiento}" },
                puntoSubida = puntoSubida,
                horaSubida = horaSubida,
                mapsUrl = mapsUrl,
                mapsUrlBajada = mapsBajada[p.parada].orEmpty()
            )
        }
        if (esPrueba) features.booking.marcarBoletosComoPrueba(boletos) else boletos
    } catch (e: Exception) {
        null
    }
}

/**
 * Descarga/imprime los boletos (diálogo del navegador → "Guardar como PDF").
 * Hoja 1: el/los boleto(s). Hoja 2: Términos y Condiciones (salto de página).
 */
suspend fun descargarBoletosDeSesion(sessionId: String): Boolean {
    val boletos = construirBoletosHtmlDeSesion(sessionId) ?: return false
    val terminosPagina = "<div style=\"page-break-before:always;\">${features.booking.generarHtmlTerminos()}</div>"
    abrirHtmlParaImprimir(features.booking.envolverBoletosParaImprimir(boletos + terminosPagina))
    return true
}
